import { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Checkbox, Collapse, Space, Tag, Typography, message, theme } from 'antd';
import { errorMessage, space } from '@viq-admin/ui';
import { CRM_STAGE_STRIP_HEIGHT } from '../types';
import { commitInChunks } from './chunkedApi';
import { CRM_COPY } from './copy';
import { HelpHint, HelpPopover, HintLine } from './HelpHint';
import { SendActionRail } from './SendActionRail';
import { SendReadyList } from './SendReadyList';
import { OUTCOME_TAG_COLOR, everyLeadWentThrough, selectionSummaryText, unreadyReason } from './sendSummary';
import { byDisplayName } from './sortRows';
import { commitBodyFrom, isReady } from './useCrmQueue';
import type {
  CommitOutcome,
  CrmQueueApi,
  MergePolicy,
  PersonPreview,
  PreviewSummary,
} from './types';
import type { CrmQueueAction, CrmQueueState } from './useCrmQueue';

export interface SendViewProps {
  conferenceId: string;
  api: CrmQueueApi;
  source: string;
  previews: PersonPreview[];
  state: CrmQueueState;
  dispatch: React.Dispatch<CrmQueueAction>;
  canManage: boolean;
  /** `PreviewResponse.policy` — the tenant default, shown when there's no per-run override. */
  tenantPolicy: MergePolicy;
  /**
   * `PreviewResponse.summary` — drives TWO of the four above-the-button
   * disclosures (p3 Task 4, spec 1 §3 step 5 "reported, never silent" / §4
   * resolution (b)): `needsEmail` and `ownersUnavailable`. The two OWNER
   * counts are deliberately derived from `previews` instead, because the
   * backend accumulates its `integrationAccountOwned`/`ownerUnverified`
   * terms with no `ownerRequired` gate — see the derivation in the body.
   * Undefined until the preview lands, the same moment `previews` is still
   * `[]` for the reason `loading` above documents. Both fields read from it
   * are themselves optional deploy-skew fields (an older backend's response
   * won't carry them) — the render below treats absence and 0/`false`
   * identically (nothing shown), so there is no separate "unknown" state to
   * draw.
   */
  summary?: PreviewSummary;
  /**
   * The live CRM read behind this view is still in flight, so `previews` is
   * empty for a reason that is not "nothing is ready". Opening Send is one of
   * the two things that STARTS that read, so an operator who comes straight
   * here lands on this state.
   */
  loading?: boolean;
  /** Parent invalidates match-check + preview queries (with `force`, for Recheck). */
  onCommitted: () => void;
}

// The outcome colours, the not-ready reason and the summary sentence live in
// `./sendSummary` (port plan step 6) so the unified grid's Send lens words a
// send exactly as this view does.
const TAG_COLOR = OUTCOME_TAG_COLOR;

/**
 * Send view (p3 Task 5) — the final step of Clean/Match/Send. Selection
 * defaults to every ready row (`isReady`, via the shared `useCrmQueue`
 * reducer's `deselected` set, which starts empty) so there's nothing to
 * opt IN to, only OUT of. Not-ready rows are listed with a plain reason,
 * purely informational — browsing is never blocked. The per-run policy
 * override lives at the top of Match (gh-31); the CRM source picker lives in
 * the stage strip above every tab, not here. Commit is a plain async call +
 * local `busy` state, matching the package's no-`useMutation` idiom (see
 * `SalesforceExportDialog`); the results panel renders every row the engine
 * returned, no truncation — the legacy `.slice(0, 10)` behavior this view
 * replaces was a named defect.
 */
export function SendView({
  conferenceId,
  api,
  source,
  previews,
  state,
  dispatch,
  canManage,
  tenantPolicy,
  summary,
  loading,
  onCommitted,
}: SendViewProps) {
  const [busy, setBusy] = useState(false);
  const [outcome, setOutcome] = useState<CommitOutcome | null>(null);
  // Snapshot of `previews`' names taken AT SEND TIME, before `onCommitted()`
  // invalidates the preview query. The refetch runs under `scope: 'changed'`
  // and a lead that was just sent is no longer "changed", so it drops out of
  // `previews` — the live prop can no longer resolve a name for exactly the
  // rows the results panel is about. This snapshot is what `resultRows` below
  // reads first, so the panel keeps showing a name instead of falling all the
  // way through to the bare id.
  const [sentNames, setSentNames] = useState<Map<string, string> | null>(null);
  const { token } = theme.useToken();
  // The panes' own container — restored on Back, so this is where a Back
  // click's focus target lives once it remounts (see the effect below).
  const panesRef = useRef<HTMLDivElement>(null);
  // Set true by the Back button's click handler, consumed by the effect
  // below the very next time `outcome` becomes null. A plain boolean rather
  // than deriving from `outcome` transitions directly: the initial mount
  // also has `outcome === null` and must NOT steal focus then.
  const focusPanesOnReturn = useRef(false);

  // `.filter()` hands back a fresh array, so the in-place sort never reaches
  // the `previews` prop — which `commitBodyFrom` still reads verbatim, so the
  // ordering here can only change what is DISPLAYED, never what is sent.
  const readyRows = useMemo(() => previews.filter(isReady).sort(byDisplayName), [previews]);
  const unreadyRows = useMemo(
    () => previews.filter((p) => !isReady(p)).sort(byDisplayName),
    [previews],
  );
  const selectedPreviews = useMemo(
    () => readyRows.filter((p) => !state.deselected[p.personId]),
    [readyRows, state.deselected],
  );
  // Tri-state for the select-all box. `allSelected` requires a non-empty
  // list so an empty queue reads as an empty box rather than a ticked one
  // claiming everything is included.
  const allReadySelected = readyRows.length > 0 && selectedPreviews.length === readyRows.length;
  const someReadySelected = selectedPreviews.length > 0 && !allReadySelected;

  // What this run would ACTUALLY commit under — the per-run toggle when set,
  // the tenant default otherwise. Drives the policy line's wording AND the
  // overwrite summary segment, so the two can't drift (review fix C1).
  const effectivePolicy: MergePolicy = state.policyOverride ?? tenantPolicy;

  const { fieldChoices, ownerPicks } = state;
  const summaryText = useMemo(
    () => selectionSummaryText(selectedPreviews, { fieldChoices, ownerPicks }, effectivePolicy),
    [selectedPreviews, fieldChoices, ownerPicks, effectivePolicy],
  );

  // The four above-the-button disclosures (p3 Task 4). None of them is
  // filtered by the current selection the way `summaryText` above is — these
  // are facts about what the CRM read found, not about what is currently
  // ticked.
  //
  // `needsEmail` and `ownersUnavailable` are read off the preview response's
  // frozen `summary`, where they are right: the first counts rows never
  // checked against the CRM, the second is a property of the request itself.
  // `?? 0` treats a `0` count and an `undefined` (older-backend deploy-skew)
  // identically — both render nothing, per the same zero-segment-omission
  // convention `buildSummary` already follows — and `ownersUnavailable` is
  // read with a strict `=== true` for the same reason: `undefined` must never
  // be read as `true`.
  const needsEmailCount = summary?.needsEmail ?? 0;
  const ownersUnavailable = summary?.ownersUnavailable === true;

  // The two OWNER disclosures are deliberately NOT read off `summary`
  // (fix round 2, Critical). `summary.integrationAccountOwned` and
  // `summary.ownerUnverified` are accumulated by the backend with no
  // `ownerRequired` gate — `resolveOwner` runs on every row and the reducer
  // counts every row it flags (viq-backend `shared/src/crm/engine/
  // preview.ts`), "even for a row that doesn't strictly need one". So those
  // two terms include `existing` rows that already have a real human owner
  // and `ambiguous` rows the commit writes nothing for. For every one of
  // those rows `commitBodyFrom` sends no `ownerId` at all, the CRM keeps the
  // owner it has, and "N leads will be owned by the integration account —
  // nobody will be assigned to follow up" is simply false. On a day-2
  // conference with 200 rep-owned `existing` rows and no configured defaults
  // — today's default state for every tenant — that line said 200.
  //
  // Derived here from the rows instead, under the same predicate the rail
  // badge's owner term uses (`CrmViews`' `unresolvedOwnerCount`) and the same
  // one `OwnerPicker` gates its D9 warning on (its `!ownerRequired` early
  // return): `ownerRequired &&` the provenance/unverified test. That keeps
  // the three surfaces — this disclosure, the badge, and Match's per-row
  // outline — in sync BY CONSTRUCTION rather than by three separate rules
  // agreeing today, and makes them deploy-skew-safe for free: an older
  // backend omits `ownerProvenance`/`ownerUnverified` per row, and the strict
  // `===` comparisons count `undefined` as nothing, never as a match.
  //
  // The two are counted separately rather than as one disjunction because
  // they render as two distinct sentences; in practice they are mutually
  // exclusive (`ownerUnverified` only ever describes a CONFIGURED default,
  // and `integration_account` means no default was configured at all), so
  // together they still sum to the badge's single term.
  const integrationAccountOwnedCount = useMemo(
    () =>
      previews.filter((p) => p.ownerRequired && p.ownerProvenance === 'integration_account').length,
    [previews],
  );
  const ownerUnverifiedCount = useMemo(
    () => previews.filter((p) => p.ownerRequired && p.ownerUnverified === true).length,
    [previews],
  );
  // A `CommitResultRow` carries no name of its own, so the name it is rendered
  // under is resolved FIRST and the projection is what gets alphabetized —
  // sorting the raw rows would have nothing to sort by. The lookup map also
  // retires an O(n²) `find` that used to run per rendered row.
  const resultRows = useMemo(() => {
    if (!outcome) return [];
    // Live `previews` first would be the bug this replaces: by the time this
    // renders, `onCommitted()`'s refetch has usually already dropped the
    // just-sent rows out of `previews`. The snapshot taken at send time is
    // checked FIRST for exactly that reason; the live prop remains a fallback
    // for a row that never made it into the snapshot; the id is the last
    // resort — a name beats an id, and an id beats a blank.
    const nameById = new Map(previews.map((p) => [p.personId, p.displayName]));
    return outcome.results
      .map((r) => ({
        ...r,
        displayName: sentNames?.get(r.personId) ?? nameById.get(r.personId) ?? r.personId,
      }))
      .sort(byDisplayName);
  }, [outcome, previews, sentNames]);

  // Read-only outranks an empty selection: with no `manage:CrmIntegration`
  // every checkbox is disabled too, so "tick something" would be advice the
  // operator cannot take. A still-running look-up outranks it in turn — there
  // is nothing above to tick yet, so "Nothing is ticked" would read as the
  // operator's oversight rather than as a wait.
  const sendDisabledReason = !canManage
    ? CRM_COPY.help.sendDisabledReadOnly.text
    : loading
      ? CRM_COPY.previewLoadingLine
      : selectedPreviews.length === 0
        ? CRM_COPY.help.sendDisabledNoSelection.text
        : null;

  // Runs the render after Back clears `outcome`, once the panes are back in
  // the DOM — a synchronous focus call inside the click handler would find
  // nothing, since the panes don't remount until this state update commits.
  // Targets the ready-list heading: always present (unlike `send-select-all`,
  // which is disabled — and unfocusable — whenever nothing is ready), so this
  // never silently no-ops.
  useEffect(() => {
    if (outcome === null && focusPanesOnReturn.current) {
      focusPanesOnReturn.current = false;
      panesRef.current?.querySelector<HTMLElement>('[data-testid="send-ready-heading"]')?.focus();
    }
  }, [outcome]);

  async function onSend() {
    setBusy(true);
    try {
      const body = commitBodyFrom({ previews, state });
      // Snapshot names off `previews` NOW, while it still holds them — see
      // the `sentNames` declaration above for why the live prop can't be
      // trusted by the time the results panel renders.
      setSentNames(new Map(previews.map((p) => [p.personId, p.displayName])));
      // Through the body-cap chunker, never `api.commit` directly — a
      // selection can exceed what one commit body may carry.
      const result = await commitInChunks(api, conferenceId, source, body);
      setOutcome(result);
      if (everyLeadWentThrough(result)) {
        void message.success(CRM_COPY.sendSuccessToast);
      } else {
        void message.warning(CRM_COPY.sendPartialFailureToast);
      }
      // Only the rows that actually went out are spent — everything held back
      // keeps its picks and its deselection (review fix I2).
      dispatch({ type: 'resetAfterSend', sentIds: body.persons.map((row) => row.personId) });
      onCommitted();
    } catch (err) {
      // Spec 2026-09-28 §7: a 409 is a stop someone can act on (several
      // Salesforce Opportunities match this event, say), and its message says
      // what to do. It can run two sentences with Opportunity Ids, so it gets
      // a longer duration than antd's 3s default; the generic toast for
      // everything else keeps that default.
      const conflict = (err as { statusCode?: number } | null)?.statusCode === 409;
      if (conflict) {
        void message.error({ content: errorMessage(err, CRM_COPY.sendFailedToast), duration: 10 });
      } else {
        void message.error(CRM_COPY.sendFailedToast);
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div data-testid={`crm-send-view-${conferenceId}-${source}`}>
      <HintLine text={CRM_COPY.help.tabSend.text} testId="crm-hint-tab-send" />
      {/* Every section header keeps its count in its own text node — each help
          trigger is a SIBLING of the Title, never a child of it.

          Two panes, side by side: the ready list scrolls in its own region on
          the left, the action rail sticks on the right so Send is never
          scrolled below the fold. Rendered only when there is no `outcome` —
          once a send has landed, the results block below takes the full
          width on its own.

          `flexWrap` + `minWidth` and no media query, no antd responsive prop,
          no `Grid.useBreakpoint`: a partial antd responsive map lets antd's
          own defaults win above its largest declared breakpoint, and this
          repo's jsdom `matchMedia` stub answers every query identically, so
          that bug class tests green and ships broken. Under the widths below
          the rail simply wraps beneath the list. */}
      {!outcome && (
        <div
          ref={panesRef}
          data-testid="crm-send-panes"
          style={{ display: 'flex', flexWrap: 'wrap', gap: space.lg, alignItems: 'flex-start' }}
        >
          {/* Grows to fill, but never below a readable width — under that the
              rail wraps beneath instead of both panes getting squeezed. */}
          {/* `min()`: on a phone the readable width is the whole screen. */}
          <div style={{ flex: '1 1 480px', minWidth: 'min(320px, 100%)' }}>
            <SendReadyList
              readyRows={readyRows}
              deselected={state.deselected}
              selectedCount={selectedPreviews.length}
              allSelected={allReadySelected}
              someSelected={someReadySelected}
              canManage={canManage}
              loading={loading}
              dispatch={dispatch}
            />
            {unreadyRows.length > 0 && (
              // One Popover for all three reasons — each names the fix and
              // the tab to make it on. A tooltip per row would repeat the
              // same three sentences down the whole list.
              //
              // The Popover trigger is a flex sibling of the Collapse, never
              // a child of its `label`: a popover trigger inside an antd
              // Collapse label toggles the panel on click, so it sits beside
              // the header row instead.
              <div style={{ display: 'flex', alignItems: 'center', gap: space.xs, marginTop: space.md }}>
                <Collapse
                  ghost
                  style={{ flex: 1 }}
                  items={[
                    {
                      key: 'not-ready',
                      label: (
                        <Typography.Text strong>
                          {`${CRM_COPY.notReadySectionHeader} (${unreadyRows.length})`}
                        </Typography.Text>
                      ),
                      children: (
                        <Space direction="vertical" size={space.sm} style={{ width: '100%' }}>
                          {unreadyRows.map((p) => (
                            <Space
                              key={p.personId}
                              size={space.sm}
                              data-testid={`send-unready-row-${p.personId}`}
                            >
                              <Checkbox checked={false} disabled aria-label={`Include ${p.displayName}`} />
                              <Typography.Text>{p.displayName}</Typography.Text>
                              <Typography.Text type="secondary">{unreadyReason(p)}</Typography.Text>
                            </Space>
                          ))}
                        </Space>
                      ),
                    },
                  ]}
                />
                <HelpPopover {...CRM_COPY.help.sendNotReady} testId="crm-help-send-not-ready" />
              </div>
            )}
          </div>
          <div
            data-testid="crm-send-rail"
            style={{
              flex: '0 1 320px',
              minWidth: 'min(280px, 100%)',
              position: 'sticky',
              // Fix round 1, Important: measured from the same scroll
              // container as `BatchScreen`'s pinned `crm-tab-toolbar` strip
              // above it — `space.lg` alone (the old value) no longer
              // accounts for that strip, and the rail tucked under it.
              top: CRM_STAGE_STRIP_HEIGHT + space.lg,
              // Below about 624px of pane width the rail becomes the LAST
              // flex line and wraps beneath the list (see the block comment
              // above `crm-send-panes`) — but it keeps `position: sticky`,
              // so once the operator scrolls with Not-ready expanded it pins
              // in place OVER the list's text (fix round, Minor). An opaque
              // background stops the list showing through; the same token
              // the stage strip paints itself with (`colorBgLayout`, not
              // `colorBgContainer` — this rail isn't a card, it sits on the
              // page surface like the strip does). `zIndex` only needs to
              // beat ordinary in-flow content (default stacking, no explicit
              // z-index), so a small value is enough — it is deliberately
              // far below the strip's own `zIndex: 4`, which the two never
              // actually need to contest.
              background: token.colorBgLayout,
              zIndex: 1,
            }}
          >
            <SendActionRail
              effectivePolicy={effectivePolicy}
              summaryText={summaryText}
              needsEmailCount={needsEmailCount}
              integrationAccountOwnedCount={integrationAccountOwnedCount}
              ownerUnverifiedCount={ownerUnverifiedCount}
              ownersUnavailable={ownersUnavailable}
              sendDisabledReason={sendDisabledReason}
              busy={busy}
              onSend={() => void onSend()}
            />
          </div>
        </div>
      )}

      {outcome && (
        <div style={{ marginTop: space.lg }}>
          {/* One glossary for all seven outcomes. A tooltip per Tag would
              repeat up to seven explanations down every row. */}
          <Space size={space.xs} align="center" style={{ marginBottom: space.sm }}>
            <Typography.Title level={5} style={{ margin: 0 }}>
              {`${CRM_COPY.resultsHeader} (${outcome.results.length})`}
            </Typography.Title>
            <HelpPopover {...CRM_COPY.help.sendResults} testId="crm-help-send-results" />
            {/* The results replace the panes, so this is the only way back to
                the rows the operator deliberately held back from this batch.
                `resetAfterSend` already spent the JUST-SENT rows' field/owner
                picks, but deliberately leaves their Send-selection alone (I2)
                — so on its own, clearing `outcome` here would bring the panes
                back with those rows still ticked, against whatever the
                invalidated preview refetch happens to show for them (which,
                mid-refetch or on a failed refetch, is the STALE pre-send
                data). A second click of Send would then re-commit the same
                rows — a double write to the customer's CRM (fix round,
                Important). So this ALSO deselects exactly the ids this commit
                actually sent — read out of `outcome.results` before it's
                cleared — leaving every row the operator held back untouched. */}
            <Button
              data-testid="crm-send-back-to-list"
              onClick={() => {
                const sentIds = outcome.results.map((r) => r.personId);
                if (sentIds.length > 0) {
                  dispatch({ type: 'setAllSelected', personIds: sentIds, selected: false });
                }
                focusPanesOnReturn.current = true;
                setOutcome(null);
                setSentNames(null);
              }}
            >
              {CRM_COPY.backToListButton}
            </Button>
          </Space>
          <Space direction="vertical" size={space.sm} style={{ width: '100%' }}>
            {resultRows.map((r) => (
              <Space
                key={r.personId}
                direction="vertical"
                size={space.xs}
                data-testid={`send-result-${r.personId}`}
              >
                <Space size={space.sm}>
                  <Typography.Text strong>{r.displayName}</Typography.Text>
                  <Tag color={TAG_COLOR[r.outcome]}>
                    {r.outcome === 'incomplete' && r.heldFields && r.heldFields.length > 0
                      ? CRM_COPY.incompleteHeldLabel
                      : CRM_COPY.outcomeLabel[r.outcome]}
                  </Tag>
                  {r.outcome === 'changed_since_preview' && (
                    <>
                      <Button size="small" onClick={() => onCommitted()} aria-label={`${CRM_COPY.recheckButton} ${r.displayName}`}>
                        {CRM_COPY.recheckButton}
                      </Button>
                      <HelpHint
                        {...CRM_COPY.help.sendRecheck}
                        testId={`crm-help-send-recheck-${r.personId}`}
                      />
                    </>
                  )}
                </Space>
                {r.reason && (
                  <Typography.Text type={r.outcome === 'failed' ? 'danger' : r.outcome === 'incomplete' ? 'warning' : 'secondary'}>
                    {r.reason}
                  </Typography.Text>
                )}
                {/* A sub-line, not another Tag: the person write and the
                    activity push succeed independently, so a row can be
                    `created` AND have lost a follow-up. Folding that into the
                    outcome Tag would force one of the two facts to win, and
                    the glossary above is written for exactly seven outcomes. */}
                {r.activities && (
                  <Space size={space.xs} wrap data-testid={`send-activities-${r.personId}`}>
                    {r.activities.logged > 0 && (
                      <Typography.Text type="secondary">
                        {CRM_COPY.activity.logged(r.activities.logged)}
                      </Typography.Text>
                    )}
                    {r.activities.failed > 0 && (
                      <Typography.Text type="warning">
                        {CRM_COPY.activity.failed(r.activities.failed)}
                      </Typography.Text>
                    )}
                  </Space>
                )}
                {r.eventLink && CRM_COPY.eventLinkNote[r.eventLink] && (
                  <Typography.Text type="secondary" data-testid={`send-event-link-${r.personId}`}>
                    {CRM_COPY.eventLinkNote[r.eventLink]}
                  </Typography.Text>
                )}
                {r.taskOwner === 'integration_account' && (
                  <Typography.Text type="warning" data-testid={`send-task-owner-${r.personId}`}>
                    {CRM_COPY.taskOwnerFallback}
                  </Typography.Text>
                )}
              </Space>
            ))}
          </Space>
        </div>
      )}
    </div>
  );
}
